import { useState } from "react";
import { toast } from "sonner";
import { Radar, Activity, FileText, Cpu, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "./ui";

function renderText(text) {
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-2" />;
    const isHeading = /^(#{1,3}\s|[A-Z][A-Za-z ]+:$|\d+\)\s)/.test(trimmed) || /^\*\*.+\*\*$/.test(trimmed);
    const clean = trimmed.replace(/^#+\s/, "").replace(/\*\*/g, "").replace(/^[-*]\s/, "• ");
    return (
      <p key={i} className={isHeading ? "font-heading font-bold text-foreground mt-4 mb-1 tracking-tight" : "text-sm text-muted-foreground leading-relaxed"}>
        {clean}
      </p>
    );
  });
}

const CARDS = [
  { key: "threat", endpoint: "/ai/threat-analysis", icon: Radar, title: "Threat Analysis", desc: "Reason over active threats and prioritize remediation.", accent: "#F23F2C", roles: ["admin", "compliance", "auditor"] },
  { key: "anomaly", endpoint: "/ai/anomaly-detection", icon: Activity, title: "Backup Anomaly Scan", desc: "Detect stale backups and unusual fleet behavior.", accent: "#00F0FF", roles: ["admin", "compliance", "auditor"] },
  { key: "report", endpoint: "/ai/compliance-report", icon: FileText, title: "21 CFR Part 11 Report", desc: "Generate an inspection-ready compliance summary.", accent: "#00FF66", roles: ["admin", "compliance"] },
];

export default function AIInsights() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);

  const run = async (card) => {
    setActive(card.key);
    setLoading(true);
    setStreaming(false);
    setOutput("");
    try {
      const token = localStorage.getItem("vaultvec_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${card.endpoint}/stream`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      setLoading(false);
      setStreaming(true);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setOutput(acc);
      }
    } catch (e) {
      toast.error("Analysis failed. Please try again.");
      setActive(null);
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.post(
        "/reports/pdf",
        { title: "21 CFR Part 11 Compliance Report", content: output },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "vaultvec-compliance-report.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cards = CARDS.filter((c) => c.roles.includes(user.role));

  return (
    <div>
      <PageHeader eyebrow="VAULTVEC AI" title="AI Insights" subtitle="Claude-powered analysis for regulated infrastructure" />
      <div className="p-8 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-4">
          {cards.map((c) => (
            <button
              key={c.key}
              data-testid={`ai-run-${c.key}`}
              onClick={() => run(c)}
              disabled={loading || streaming}
              className={`w-full text-left rounded-sm p-5 transition-all ${active === c.key ? "border border-primary bg-primary/10 glow-primary" : "card-elevated"}`}
            >
              <div className="h-10 w-10 rounded-sm flex items-center justify-center bg-white/5 border border-border mb-3">
                <c.icon className="h-5 w-5" style={{ color: c.accent }} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold tracking-tight">{c.title}</h3>
              <p className="text-xs text-muted-foreground mt-1.5">{c.desc}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 card-elevated rounded-sm min-h-[400px] flex flex-col">
          <div className="border-b border-border px-5 py-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Cpu className="h-4 w-4 text-primary" />
              <span className="font-mono text-[11px] tracking-widest text-muted-foreground">VAULTVEC AI OUTPUT</span>
            </div>
            {active === "report" && output && !streaming && !loading && (
              <Button data-testid="download-pdf-btn" size="sm" onClick={downloadPdf} className="rounded-none bg-[hsl(var(--chart-2))] hover:bg-[hsl(var(--chart-2))]/90 text-black text-xs h-7">
                <Download className="h-3.5 w-3.5 mr-1" /> Download PDF
              </Button>
            )}
          </div>
          <div className="p-6 flex-1" data-testid="ai-output">
            {loading && (
              <div className="flex items-center gap-3 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="font-mono text-xs tracking-widest">ANALYZING…</span>
              </div>
            )}
            {!loading && !output && <p className="text-sm text-muted-foreground">Select an analysis on the left to run Vaultvec AI.</p>}
            {output && (
              <div>
                {renderText(output)}
                {streaming && <span className="inline-block w-2 h-4 bg-primary ml-1 pulse-dot align-middle" />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
